import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import { paymentAPI } from '../../services/api';
import { CURRENCIES, VALIDATION_RULES } from '../../utils/constants';
import ServiceSelector from '../service/ServiceSelector';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const CreatePaymentForm = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useAdminTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [flightLocations, setFlightLocations] = useState([]);
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    phoneNumber: '',
    currency: 'GBP', // Default to GBP for UK services
    description: '',
    flightDetails: {
      from: '',
      to: '',
      flightNumber: '',
      departureDate: '',
      arrivalDate: ''
    }
  });
  const [errors, setErrors] = useState({});
  
  // Get current date-time in local timezone for min attribute
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  
  // Get minimum arrival date based on departure date
  const getMinArrivalDate = () => {
    if (formData.flightDetails.departureDate) {
      return formData.flightDetails.departureDate;
    }
    return getCurrentDateTime();
  };

  // Load flight locations on component mount
  useEffect(() => {
    const loadFlightLocations = async () => {
      try {
        const response = await paymentAPI.getFlightLocations();
        if (response.success) {
          setFlightLocations(response.data);
        }
      } catch (error) {
        console.error('Failed to load flight locations:', error);
      }
    };

    loadFlightLocations();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Client name validation
    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    } else if (formData.clientName.trim().length < VALIDATION_RULES.CLIENT_NAME.MIN_LENGTH) {
      newErrors.clientName = `Name must be at least ${VALIDATION_RULES.CLIENT_NAME.MIN_LENGTH} characters`;
    } else if (formData.clientName.trim().length > VALIDATION_RULES.CLIENT_NAME.MAX_LENGTH) {
      newErrors.clientName = `Name must be no more than ${VALIDATION_RULES.CLIENT_NAME.MAX_LENGTH} characters`;
    }

    // Email validation
    if (!formData.clientEmail.trim()) {
      newErrors.clientEmail = 'Client email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail.trim())) {
      newErrors.clientEmail = 'Please enter a valid email address';
    }

    // Service selection validation
    if (!selectedService) {
      newErrors.service = 'Please select a service type and level';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < VALIDATION_RULES.DESCRIPTION.MIN_LENGTH) {
      newErrors.description = `Description must be at least ${VALIDATION_RULES.DESCRIPTION.MIN_LENGTH} characters`;
    } else if (formData.description.trim().length > VALIDATION_RULES.DESCRIPTION.MAX_LENGTH) {
      newErrors.description = `Description must be no more than ${VALIDATION_RULES.DESCRIPTION.MAX_LENGTH} characters`;
    }

    // Flight details validation
    if (!formData.flightDetails.from) {
      newErrors['flightDetails.from'] = 'Origin airport is required';
    }

    if (!formData.flightDetails.to) {
      newErrors['flightDetails.to'] = 'Destination airport is required';
    }

    if (!formData.flightDetails.flightNumber.trim()) {
      newErrors['flightDetails.flightNumber'] = 'Flight number is required';
    } else if (formData.flightDetails.flightNumber.trim().length < 2) {
      newErrors['flightDetails.flightNumber'] = 'Flight number must be at least 2 characters';
    }

    if (!formData.flightDetails.departureDate) {
      newErrors['flightDetails.departureDate'] = 'Departure date is required';
    } else {
      const departureDate = new Date(formData.flightDetails.departureDate);
      const now = new Date();
      if (departureDate < now) {
        newErrors['flightDetails.departureDate'] = 'Departure date cannot be in the past';
      }
    }

    if (!formData.flightDetails.arrivalDate) {
      newErrors['flightDetails.arrivalDate'] = 'Arrival date is required';
    } else {
      const arrivalDate = new Date(formData.flightDetails.arrivalDate);
      const departureDate = formData.flightDetails.departureDate ? new Date(formData.flightDetails.departureDate) : null;
      
      if (departureDate && arrivalDate < departureDate) {
        newErrors['flightDetails.arrivalDate'] = 'Arrival date must be after departure date';
      }
    }

    // Currency validation
    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    } else if (!CURRENCIES.find(c => c.value === formData.currency)) {
      newErrors.currency = 'Please select a valid currency';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested flight details
    if (name.startsWith('flightDetails.')) {
      const flightField = name.split('.')[1];
      
      // Special handling for departure date changes
      if (flightField === 'departureDate') {
        setFormData(prev => {
          const newFlightDetails = {
            ...prev.flightDetails,
            departureDate: value
          };
          
          // If arrival date is before new departure date, clear it
          if (prev.flightDetails.arrivalDate && value && new Date(prev.flightDetails.arrivalDate) < new Date(value)) {
            newFlightDetails.arrivalDate = '';
            // Clear arrival date error if exists
            setErrors(prevErrors => ({
              ...prevErrors,
              'flightDetails.arrivalDate': ''
            }));
          }
          
          return {
            ...prev,
            flightDetails: newFlightDetails
          };
        });
      } else {
        setFormData(prev => ({
          ...prev,
          flightDetails: {
            ...prev.flightDetails,
            [flightField]: value
          }
        }));
      }
      
      // Clear nested field error when user starts typing
      const errorKey = `flightDetails.${flightField}`;
      if (errors[errorKey]) {
        setErrors(prev => ({
          ...prev,
          [errorKey]: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  const handleServiceSelect = useCallback((service) => {
    setSelectedService(service);
    
    // Clear service error when service is selected
    if (errors.service) {
      setErrors(prev => ({
        ...prev,
        service: ''
      }));
    }

    // Auto-generate description based on selected service
    if (service && !formData.description) {
      const description = `${service.category.name} ${service.level.name} Service - ${service.level.description}`;
      setFormData(prev => ({
        ...prev,
        description: description
      }));
    }
  }, [errors.service, formData.description]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    setIsLoading(true);

    try {
      // Prepare service details
      const serviceDetails = {
        categoryId: selectedService.category.id,
        categoryName: selectedService.category.name,
        levelId: selectedService.level.id,
        levelName: selectedService.level.name,
        basePrice: selectedService.pricing.basePrice,
        additionalServices: {
          bags: {
            count: selectedService.additionalServices?.bags || 1,
            range: selectedService.level.additionalServices?.bags ? 
              Object.keys(selectedService.level.additionalServices.bags).find(range => {
                const bagCount = selectedService.additionalServices?.bags || 1;
                const [min, max] = range.split('-').map(Number);
                return bagCount >= min && bagCount <= max;
              }) : '1-4',
            fee: selectedService.pricing.additionalFees || 0
          }
        },
        features: selectedService.level.features || [],
        pricingSource: selectedService.pricing.pricingSource || 'static'
      };

      // Prepare price breakdown
      const priceBreakdown = {
        basePrice: selectedService.pricing.basePrice,
        additionalFees: selectedService.pricing.additionalFees || 0,
        totalPrice: selectedService.pricing.totalPrice,
        currency: selectedService.pricing.currency
      };

      const response = await paymentAPI.createPaymentRequest({
        clientName: formData.clientName.trim(),
        clientEmail: formData.clientEmail.trim().toLowerCase(),
        phoneNumber: formData.phoneNumber.trim() || undefined,
        amount: selectedService.pricing.totalPrice,
        currency: selectedService.pricing.currency,
        serviceType: selectedService.category.name.toUpperCase(),
        description: formData.description.trim(),
        serviceDetails: serviceDetails,
        priceBreakdown: priceBreakdown,
        flightDetails: {
          from: formData.flightDetails.from,
          to: formData.flightDetails.to,
          flightNumber: formData.flightDetails.flightNumber,
          departureDate: formData.flightDetails.departureDate,
          arrivalDate: formData.flightDetails.arrivalDate
        }
      });

      if (response.success) {
        toast.success('Payment request created successfully!');
        navigate('/admin/dashboard');
      } else {
        toast.error(response.message || 'Failed to create payment request');
      }
    } catch (error) {
      console.error('Error creating payment request:', error);
      toast.error(error.response?.data?.message || 'Failed to create payment request');
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className={`card transition-colors duration-300 ${
        isDarkMode ? 'card-dark' : 'card'
      }`}>
        <div className={`card-header transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-xl font-semibold transition-colors duration-300 ${
            isDarkMode ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Create Airport Meet & Greet Payment Request
          </h2>
          <p className={`mt-1 text-sm transition-colors duration-300 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Select a service and send a payment link to your client via email
          </p>
        </div>

        <div className={`card-body transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
        }`}>
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Service Selection */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>Service Selection</h3>
              <ServiceSelector 
                onServiceSelect={handleServiceSelect}
                selectedService={selectedService}
                isDarkMode={isDarkMode}
              />
              {errors.service && (
                <p className="form-error mt-2">{errors.service}</p>
              )}
            </div>

            {/* Client Information */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>Client Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Client Name */}
                <div className="form-group">
                  <label htmlFor="clientName" className={`label transition-colors duration-300 ${
                    isDarkMode ? 'label-dark' : 'label'
                  }`}>
                    Client Name *
                  </label>
                  <input
                    type="text"
                    id="clientName"
                    name="clientName"
                    value={formData.clientName}
                    onChange={handleInputChange}
                    className={`transition-colors duration-300 ${
                      isDarkMode ? 'input-dark' : 'input'
                    } ${errors.clientName ? 'input-error' : ''}`}
                    placeholder="Enter client's full name"
                    disabled={isLoading}
                  />
                  {errors.clientName && (
                    <p className="form-error">{errors.clientName}</p>
                  )}
                </div>

                {/* Client Email */}
                <div className="form-group">
                  <label htmlFor="clientEmail" className={`label transition-colors duration-300 ${
                    isDarkMode ? 'label-dark' : 'label'
                  }`}>
                    Client Email *
                  </label>
                  <input
                    type="email"
                    id="clientEmail"
                    name="clientEmail"
                    value={formData.clientEmail}
                    onChange={handleInputChange}
                    className={`transition-colors duration-300 ${
                      isDarkMode ? 'input-dark' : 'input'
                    } ${errors.clientEmail ? 'input-error' : ''}`}
                    placeholder="Enter client's email address"
                    disabled={isLoading}
                  />
                  {errors.clientEmail && (
                    <p className="form-error">{errors.clientEmail}</p>
                  )}
                </div>
              </div>

              {/* Phone Number */}
              <div className="form-group">
                <label htmlFor="phoneNumber" className={`label transition-colors duration-300 ${
                  isDarkMode ? 'label-dark' : 'label'
                }`}>
                  Phone Number
                </label>
                <PhoneInput
                  international
                  defaultCountry="GB"
                  value={formData.phoneNumber}
                  onChange={(value) => setFormData(prev => ({ ...prev, phoneNumber: value || '' }))}
                  disabled={isLoading}
                  style={{
                    '--PhoneInput-color--focus': '#8b5cf6',
                  }}
                  className="phone-input-payment"
                />
                <style>{`
                  .phone-input-payment {
                    width: 100%;
                  }
                  .phone-input-payment input,
                  .phone-input-payment .PhoneInputInput {
                    background-color: ${isDarkMode ? '#1f2937' : '#ffffff'} !important;
                    color: ${isDarkMode ? '#f3f4f6' : '#111827'} !important;
                    border: 1px solid ${isDarkMode ? '#374151' : '#d1d5db'} !important;
                    border-radius: 0.375rem !important;
                    padding: 0.5rem 0.75rem !important;
                    width: 100% !important;
                    font-size: 0.875rem !important;
                    transition: all 0.3s ease !important;
                  }
                  .phone-input-payment input:focus,
                  .phone-input-payment .PhoneInputInput:focus {
                    outline: none !important;
                    border-color: #8b5cf6 !important;
                    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1) !important;
                  }
                  .phone-input-payment .PhoneInputCountryIcon--border {
                    border-radius: 0.25rem !important;
                  }
                  .phone-input-payment .PhoneInputCountryIcon img {
                    border-radius: 0.25rem !important;
                  }
                  .phone-input-payment .PhoneInputCountrySelect {
                    background-color: ${isDarkMode ? '#1f2937' : '#ffffff'} !important;
                    color: ${isDarkMode ? '#f3f4f6' : '#111827'} !important;
                    border-radius: 0.375rem !important;
                  }
                `}</style>
              </div>
            </div>

            {/* Payment Details */}
            {selectedService && (
              <div>
                <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>Payment Details</h3>
                <div className={`border rounded-lg p-4 transition-colors duration-300 ${
                  isDarkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Service Selected
                      </label>
                      <p className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>
                        {selectedService.category.name} - {selectedService.level.name}
                      </p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Total Amount
                      </label>
                      <p className={`text-lg font-bold transition-colors duration-300 ${
                        isDarkMode ? 'text-blue-400' : 'text-blue-600'
                      }`}>
                        {formatPrice(selectedService.pricing.totalPrice, selectedService.pricing.currency)}
                      </p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Base Price
                      </label>
                      <p className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>
                        {formatPrice(selectedService.pricing.basePrice, selectedService.pricing.currency)}
                      </p>
                    </div>
                    {selectedService.pricing.additionalFees > 0 && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Additional Fees
                        </label>
                        <p className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-100' : 'text-gray-900'
                        }`}>
                          {formatPrice(selectedService.pricing.additionalFees, selectedService.pricing.currency)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Currency Selection */}
            <div className="form-group">
              <label htmlFor="currency" className={`label transition-colors duration-300 ${
                isDarkMode ? 'label-dark' : 'label'
              }`}>
                Currency *
              </label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className={`transition-colors duration-300 ${
                  isDarkMode ? 'input-dark' : 'input'
                } ${errors.currency ? 'input-error' : ''}`}
                disabled={isLoading}
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency.value} value={currency.value}>
                    {currency.label}
                  </option>
                ))}
              </select>
              {errors.currency && (
                <p className="form-error">{errors.currency}</p>
              )}
              <p className={`form-help transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Note: Service prices are calculated in GBP. Currency conversion will be handled by Stripe.
              </p>
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description" className={`label transition-colors duration-300 ${
                isDarkMode ? 'label-dark' : 'label'
              }`}>
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className={`transition-colors duration-300 ${
                  isDarkMode ? 'input-dark' : 'input'
                } ${errors.description ? 'input-error' : ''}`}
                placeholder="Describe what this payment is for..."
                rows={4}
                disabled={isLoading}
              />
              {errors.description && (
                <p className="form-error">{errors.description}</p>
              )}
              <p className={`form-help transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {formData.description.length}/{VALIDATION_RULES.DESCRIPTION.MAX_LENGTH} characters
              </p>
            </div>

            {/* Flight Details */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>Flight Details</h3>
              <p className={`text-sm mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Select the airports for this flight service
              </p>
              
              {/* From and To Locations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label htmlFor="flightDetails.from" className={`label transition-colors duration-300 ${
                    isDarkMode ? 'label-dark' : 'label'
                  }`}>
                    From *
                  </label>
                  <select
                    id="flightDetails.from"
                    name="flightDetails.from"
                    value={formData.flightDetails.from}
                    onChange={handleInputChange}
                    className={`transition-colors duration-300 ${
                      isDarkMode ? 'input-dark' : 'input'
                    } ${errors['flightDetails.from'] ? 'input-error' : ''}`}
                    disabled={isLoading}
                  >
                    <option value="">Select origin airport</option>
                    {flightLocations.map((location) => (
                      <option key={location.value} value={location.value}>
                        {location.label}
                      </option>
                    ))}
                  </select>
                  {errors['flightDetails.from'] && (
                    <p className="form-error">{errors['flightDetails.from']}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="flightDetails.to" className={`label transition-colors duration-300 ${
                    isDarkMode ? 'label-dark' : 'label'
                  }`}>
                    To *
                  </label>
                  <select
                    id="flightDetails.to"
                    name="flightDetails.to"
                    value={formData.flightDetails.to}
                    onChange={handleInputChange}
                    className={`transition-colors duration-300 ${
                      isDarkMode ? 'input-dark' : 'input'
                    } ${errors['flightDetails.to'] ? 'input-error' : ''}`}
                    disabled={isLoading}
                  >
                    <option value="">Select destination airport</option>
                    {flightLocations.map((location) => (
                      <option key={location.value} value={location.value}>
                        {location.label}
                      </option>
                    ))}
                  </select>
                  {errors['flightDetails.to'] && (
                    <p className="form-error">{errors['flightDetails.to']}</p>
                  )}
                </div>
              </div>

              {/* Flight Number and Dates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                  <label htmlFor="flightDetails.flightNumber" className={`label transition-colors duration-300 ${
                    isDarkMode ? 'label-dark' : 'label'
                  }`}>
                    Flight Number *
                  </label>
                  <input
                    type="text"
                    id="flightDetails.flightNumber"
                    name="flightDetails.flightNumber"
                    value={formData.flightDetails.flightNumber}
                    onChange={handleInputChange}
                    className={`transition-colors duration-300 ${
                      isDarkMode ? 'input-dark' : 'input'
                    } ${errors['flightDetails.flightNumber'] ? 'input-error' : ''}`}
                    placeholder="e.g., BA123"
                    disabled={isLoading}
                  />
                  {errors['flightDetails.flightNumber'] && (
                    <p className="form-error">{errors['flightDetails.flightNumber']}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="flightDetails.departureDate" className={`label transition-colors duration-300 ${
                    isDarkMode ? 'label-dark' : 'label'
                  }`}>
                    Departure Date *
                  </label>
                  <input
                    type="datetime-local"
                    id="flightDetails.departureDate"
                    name="flightDetails.departureDate"
                    value={formData.flightDetails.departureDate}
                    onChange={handleInputChange}
                    min={getCurrentDateTime()}
                    className={`transition-colors duration-300 ${
                      isDarkMode ? 'input-dark' : 'input'
                    } ${errors['flightDetails.departureDate'] ? 'input-error' : ''}`}
                    disabled={isLoading}
                  />
                  {errors['flightDetails.departureDate'] && (
                    <p className="form-error">{errors['flightDetails.departureDate']}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="flightDetails.arrivalDate" className={`label transition-colors duration-300 ${
                    isDarkMode ? 'label-dark' : 'label'
                  }`}>
                    Arrival Date *
                  </label>
                  <input
                    type="datetime-local"
                    id="flightDetails.arrivalDate"
                    name="flightDetails.arrivalDate"
                    value={formData.flightDetails.arrivalDate}
                    onChange={handleInputChange}
                    min={getMinArrivalDate()}
                    className={`transition-colors duration-300 ${
                      isDarkMode ? 'input-dark' : 'input'
                    } ${errors['flightDetails.arrivalDate'] ? 'input-error' : ''}`}
                    disabled={isLoading}
                  />
                  {errors['flightDetails.arrivalDate'] && (
                    <p className="form-error">{errors['flightDetails.arrivalDate']}</p>
                  )}
                  {formData.flightDetails.departureDate && !formData.flightDetails.arrivalDate && (
                    <p className={`form-help transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      Arrival must be after {new Date(formData.flightDetails.departureDate).toLocaleString('en-GB', { 
                        dateStyle: 'medium', 
                        timeStyle: 'short' 
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                disabled={isLoading}
                className={`relative w-40 h-10 text-sm font-medium rounded-md border transition-colors duration-300 focus:outline-none focus:ring-2 ${
                  isDarkMode 
                    ? 'bg-skyone-inset-dark text-zinc-300 hover:bg-skyone-border-dark border-gray-600 focus:ring-gray-500' 
                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300 border-gray-300 focus:ring-gray-500'
                }`}
              >
                <span className="absolute inset-0 flex items-center justify-center">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </span>
              </button>
              <button
                type="submit"
                disabled={isLoading || !selectedService}
                className="relative w-48 h-10 text-sm font-medium rounded-md border transition-colors duration-300 bg-blue-600 text-white hover:bg-blue-700 border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <span className="absolute inset-0 flex items-center justify-center">
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 rounded-full animate-spin mr-2 border-gray-200 border-t-white"></div>
                      Creating...
                    </>
                  ) : (
                    'Create Payment Request'
                  )}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePaymentForm;